import React, { useRef, useCallback, useEffect, useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import { MindMapNode, NodeAttachment } from '../types';
import { parseMarkdown } from '../utils/markdownUtils';
import { Maximize2, ZoomIn, ZoomOut } from 'lucide-react';

const FLOW_PREFS_KEY = 'flowView_prefs_v1';

interface LinkedNode {
    id: string;
    title: string;
    linkId: string;
    linkColor?: string;
    relationType: string;
}

interface FlowViewProps {
    nodes: MindMapNode[];
    selectedNodeId: string | null;
    onSelectNode: (nodeId: string, e: React.MouseEvent) => void;
    onExpandNote: (nodeId: string) => void;
    linksByNodeId: Record<string, LinkedNode[]>;
    searchQuery?: string;
}

export interface FlowViewHandle {
    scrollToNode: (nodeId: string) => void;
}

// O(N log N) — builds children map first instead of filtering nodes per node
function buildDfsOrder(nodes: MindMapNode[]): MindMapNode[] {
    const childrenMap = new Map<string | null, MindMapNode[]>();
    nodes.forEach(n => {
        const key = n.parentId ?? null;
        if (!childrenMap.has(key)) childrenMap.set(key, []);
        childrenMap.get(key)!.push(n);
    });
    childrenMap.forEach(children =>
        children.sort((a, b) => (a.siblingOrder ?? 0) - (b.siblingOrder ?? 0))
    );
    const result: MindMapNode[] = [];
    function traverse(parentId: string | null) {
        (childrenMap.get(parentId) || []).forEach(node => {
            result.push(node);
            traverse(node.id);
        });
    }
    traverse(null);
    return result;
}

function getNodeImages(node: MindMapNode): string[] {
    return [
        ...(node.imageBase64 ? [node.imageBase64] : []),
        ...(node.drawingBase64 ? [node.drawingBase64] : []),
        ...((node.attachments || []) as NodeAttachment[])
            .filter(a => a.type === 'image')
            .map(a => a.content),
    ].filter(Boolean) as string[];
}

// ── Memoized node row — only re-renders when selection or content changes ──
const LeftNodeRow = React.memo(function LeftNodeRow({
    node, isSelected, depth, parsedContent, imgs, peerLinks, isBranchStart, idx,
    imageNodeIds, onLeftClick, onExpandNote, onSelectNode, scrollLeftTo, setRef,
}: {
    node: MindMapNode; isSelected: boolean; depth: number;
    parsedContent: string | undefined; imgs: string[] | undefined;
    peerLinks: LinkedNode[]; isBranchStart: boolean; idx: number;
    imageNodeIds: Set<string>;
    onLeftClick: (node: MindMapNode, e: React.MouseEvent) => void;
    onExpandNote: (nodeId: string) => void;
    onSelectNode: (nodeId: string, e: React.MouseEvent) => void;
    scrollLeftTo: (nodeId: string) => void;
    setRef: (el: HTMLDivElement | null) => void;
}) {
    return (
        <div
            ref={setRef}
            className={`rounded-lg px-3 py-2 cursor-pointer transition-all border-0 group relative ${
                isBranchStart ? 'mt-8' : idx === 0 ? 'mt-0' : 'mt-0.5'
            } ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-slate-800/50'}`}
            onClick={(e) => onLeftClick(node, e)}
        >
            <button
                onClick={e => { e.stopPropagation(); onExpandNote(node.id); }}
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-500 dark:text-gray-400"
                title="Mở rộng note"
            >
                <Maximize2 size={11} />
            </button>
            {node.title && (
                <h3 className={`font-semibold pr-6 leading-snug ${depth === 0 ? 'text-[1.1em]' : 'text-[1em]'} text-gray-900 dark:text-gray-100 ${parsedContent ? 'mb-1' : ''}`}>
                    {node.title}
                </h3>
            )}
            {imgs && (
                <span className="inline-flex items-center gap-1 text-[0.75em] text-blue-400 mb-1">
                    <span className="w-1 h-1 rounded-full bg-blue-400 inline-block" />
                    {imgs.length === 1 ? '1 ảnh' : `${imgs.length} ảnh`}
                </span>
            )}
            {parsedContent ? (
                <div
                    className="text-[0.9em] text-gray-600 dark:text-gray-400 leading-relaxed prose prose-sm dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: parsedContent }}
                />
            ) : !node.title ? (
                <p className="text-[0.8em] text-gray-400 italic">Note trống</p>
            ) : null}
            {peerLinks.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-gray-100 dark:border-slate-700">
                    {peerLinks.map(link => (
                        <button
                            key={link.linkId}
                            onClick={e => {
                                e.stopPropagation();
                                onSelectNode(link.id, e);
                                scrollLeftTo(link.id);
                            }}
                            className="flex items-center gap-1 text-[0.75em] px-1.5 py-0.5 rounded-full border hover:opacity-80 transition-colors"
                            style={{ borderColor: link.linkColor || '#94a3b8', color: link.linkColor || '#94a3b8', backgroundColor: `${link.linkColor || '#94a3b8'}18` }}
                        >
                            <span>↔</span>
                            <span className="max-w-[120px] truncate">{link.title || 'Untitled'}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
});

export const FlowView = React.memo(forwardRef<FlowViewHandle, FlowViewProps>(
    ({ nodes, selectedNodeId, onSelectNode, onExpandNote, linksByNodeId }, ref) => {

    const leftPanelRef = useRef<HTMLDivElement>(null);
    const rightPanelRef = useRef<HTMLDivElement>(null);   // inner scrollable div (used by scrollRightTo)
    const rightContainerRef = useRef<HTMLDivElement>(null); // outer width-controlled div
    const leftRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const rightRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const isDragging = useRef(false);

    // loadPrefs only once via lazy initializer — no localStorage read on every render
    const [rightWidth, setRightWidth] = useState<number>(() => {
        try { return JSON.parse(localStorage.getItem(FLOW_PREFS_KEY) || '{}').rightWidth ?? 360; } catch { return 360; }
    });
    const [textScale, setTextScale] = useState<number>(() => {
        try { return JSON.parse(localStorage.getItem(FLOW_PREFS_KEY) || '{}').textScale ?? 1.0; } catch { return 1.0; }
    });
    const [imageScale, setImageScale] = useState<number>(() => {
        try { return JSON.parse(localStorage.getItem(FLOW_PREFS_KEY) || '{}').imageScale ?? 1.0; } catch { return 1.0; }
    });

    useEffect(() => {
        try {
            localStorage.setItem(FLOW_PREFS_KEY, JSON.stringify({ rightWidth, textScale, imageScale }));
        } catch {}
    }, [rightWidth, textScale, imageScale]);

    // ── Memoized derived data — recomputes only when `nodes` reference changes ──

    // DFS order: O(N log N) instead of O(N²) per render
    const orderedNodes = useMemo(() => buildDfsOrder(nodes), [nodes]);

    // Image map: computed once, used in both panels and for imageNodeIds
    const nodeImageMap = useMemo(() => {
        const map = new Map<string, string[]>();
        nodes.forEach(n => {
            const imgs = getNodeImages(n);
            if (imgs.length > 0) map.set(n.id, imgs);
        });
        return map;
    }, [nodes]);

    const imageNodes = useMemo(
        () => orderedNodes.filter(n => nodeImageMap.has(n.id)),
        [orderedNodes, nodeImageMap]
    );

    const imageNodeIds = useMemo(
        () => new Set(nodeImageMap.keys()),
        [nodeImageMap]
    );

    // Depth + rootId: single O(N) pass with nodeMap for O(1) parent lookup
    const { depthMap, rootIdMap } = useMemo(() => {
        const nodeMap = new Map<string, MindMapNode>(nodes.map(n => [n.id, n]));
        const depthMap = new Map<string, number>();
        const rootIdMap = new Map<string, string>();
        nodes.forEach(n => {
            let depth = 0;
            let cur = n;
            while (cur.parentId) {
                const parent = nodeMap.get(cur.parentId);
                if (!parent) break;
                cur = parent;
                depth++;
            }
            depthMap.set(n.id, depth);
            rootIdMap.set(n.id, cur.id);
        });
        return { depthMap, rootIdMap };
    }, [nodes]);

    // parseMarkdown is expensive (markdown + KaTeX). Cache per node content.
    const parsedContentMap = useMemo(() => {
        const map = new Map<string, string>();
        nodes.forEach(n => {
            if (n.content) map.set(n.id, parseMarkdown(n.content));
        });
        return map;
    }, [nodes]);

    // Stable peer links per node — avoids new array on every render
    const peerLinksMap = useMemo(() => {
        const map = new Map<string, LinkedNode[]>();
        orderedNodes.forEach(n => {
            const peers = (linksByNodeId[n.id] || []).filter(l => l.relationType === 'peer');
            map.set(n.id, peers);
        });
        return map;
    }, [orderedNodes, linksByNodeId]);

    // Precompute isBranchStart per node — avoids sequential logic inside render
    const isBranchStartMap = useMemo(() => {
        const map = new Map<string, boolean>();
        let prevRootId: string | null = null;
        orderedNodes.forEach((node, idx) => {
            const rootId = rootIdMap.get(node.id) ?? node.id;
            map.set(node.id, idx > 0 && rootId !== prevRootId);
            prevRootId = rootId;
        });
        return map;
    }, [orderedNodes, rootIdMap]);

    // Stable ref setters per node — new function only when orderedNodes changes
    const leftRefSetters = useMemo(() => {
        const setters: Record<string, (el: HTMLDivElement | null) => void> = {};
        orderedNodes.forEach(n => { setters[n.id] = (el) => { leftRefs.current[n.id] = el; }; });
        return setters;
    }, [orderedNodes]);

    const rightRefSetters = useMemo(() => {
        const setters: Record<string, (el: HTMLDivElement | null) => void> = {};
        orderedNodes.forEach(n => { setters[n.id] = (el) => { rightRefs.current[n.id] = el; }; });
        return setters;
    }, [orderedNodes]);

    // ─────────────────────────────────────────────────────────────────────────

    const scrollContainerTo = (container: HTMLDivElement, el: HTMLDivElement) => {
        // Use offsetTop (layout coordinates) instead of getBoundingClientRect
        // (viewport coordinates) — more reliable since it doesn't depend on
        // current scroll position or viewport state.
        let offsetTop = 0;
        let cur: HTMLElement | null = el;
        while (cur && cur !== container) {
            offsetTop += cur.offsetTop;
            cur = cur.offsetParent as HTMLElement | null;
        }
        container.scrollTo({
            top: offsetTop - container.clientHeight / 2 + el.offsetHeight / 2,
            behavior: 'smooth',
        });
    };

    const scrollLeftTo = useCallback((nodeId: string) => {
        const el = leftRefs.current[nodeId];
        const container = leftPanelRef.current;
        if (el && container) scrollContainerTo(container, el);
    }, []);

    const scrollRightTo = useCallback((nodeId: string) => {
        const el = rightRefs.current[nodeId];
        const container = rightPanelRef.current;
        if (el && container) scrollContainerTo(container, el);
    }, []);

    useImperativeHandle(ref, () => ({
        scrollToNode: (nodeId: string) => {
            scrollLeftTo(nodeId);
            if (imageNodeIds.has(nodeId)) scrollRightTo(nodeId);
        },
    }), [scrollLeftTo, scrollRightTo, imageNodeIds]);

    useEffect(() => {
        if (!selectedNodeId) return;
        scrollLeftTo(selectedNodeId);
        if (imageNodeIds.has(selectedNodeId)) scrollRightTo(selectedNodeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNodeId]);

    const handleLeftClick = useCallback((node: MindMapNode, e: React.MouseEvent) => {
        onSelectNode(node.id, e);
        if (imageNodeIds.has(node.id)) scrollRightTo(node.id);
    }, [onSelectNode, imageNodeIds, scrollRightTo]);

    const handleRightClick = useCallback((node: MindMapNode, e: React.MouseEvent) => {
        onSelectNode(node.id, e);
        scrollLeftTo(node.id);
    }, [onSelectNode, scrollLeftTo]);

    const handleDividerMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        isDragging.current = true;
        const startX = e.clientX;
        const startWidth = rightWidth;
        let liveWidth = startWidth;
        const onMouseMove = (ev: MouseEvent) => {
            if (!isDragging.current) return;
            const maxWidth = window.innerWidth - 250;
            liveWidth = Math.max(150, Math.min(maxWidth, startWidth + (startX - ev.clientX)));
            // Direct DOM — zero React re-renders during drag
            if (rightContainerRef.current) rightContainerRef.current.style.width = `${liveWidth}px`;
        };
        const onMouseUp = () => {
            isDragging.current = false;
            setRightWidth(liveWidth); // single commit on release
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [rightWidth]);

    const btnClass = "w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-gray-400 transition-colors";

    return (
        <div className="flex h-full w-full overflow-hidden bg-white dark:bg-slate-900 select-none">

            {/* Left: note content */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                <div className="shrink-0 flex items-center justify-end gap-0.5 px-3 py-1 border-b border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 mt-[60px]">
                    <span className="text-[11px] text-gray-400 mr-1 font-mono">{Math.round(textScale * 100)}%</span>
                    <button onClick={() => setTextScale(s => Math.max(0.6, +(s - 0.1).toFixed(1)))} className={btnClass} title="Giảm cỡ chữ"><ZoomOut size={13} /></button>
                    <button onClick={() => setTextScale(s => Math.min(2.0, +(s + 0.1).toFixed(1)))} className={btnClass} title="Tăng cỡ chữ"><ZoomIn size={13} /></button>
                </div>

                <div ref={leftPanelRef} className="flex-1 overflow-y-auto">
                    <div className="px-5 py-4 pb-20" style={{ fontSize: `${textScale}rem` }}>
                        {orderedNodes.map((node, idx) => (
                            <LeftNodeRow
                                key={node.id}
                                node={node}
                                isSelected={selectedNodeId === node.id}
                                depth={depthMap.get(node.id) ?? 0}
                                parsedContent={parsedContentMap.get(node.id)}
                                imgs={nodeImageMap.get(node.id)}
                                peerLinks={peerLinksMap.get(node.id) ?? []}
                                isBranchStart={isBranchStartMap.get(node.id) ?? false}
                                idx={idx}
                                imageNodeIds={imageNodeIds}
                                onLeftClick={handleLeftClick}
                                onExpandNote={onExpandNote}
                                onSelectNode={onSelectNode}
                                scrollLeftTo={scrollLeftTo}
                                setRef={leftRefSetters[node.id]}
                            />
                        ))}
                        {orderedNodes.length === 0 && (
                            <div className="text-center text-gray-400 py-20 text-sm">Notebook trống.</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Draggable divider */}
            <div
                className="w-[3px] shrink-0 bg-gray-100 dark:bg-slate-800 hover:bg-blue-400 dark:hover:bg-blue-500 cursor-col-resize transition-colors active:bg-blue-500"
                onMouseDown={handleDividerMouseDown}
            />

            {/* Right: screenshots */}
            <div ref={rightContainerRef} className="flex flex-col shrink-0 overflow-hidden" style={{ width: rightWidth }}>
                <div className="shrink-0 flex items-center justify-end gap-0.5 px-3 py-1 border-b border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-950 mt-[60px]">
                    <span className="text-[11px] text-gray-400 mr-1 font-mono">{Math.round(imageScale * 100)}%</span>
                    <button onClick={() => setImageScale(s => Math.max(0.3, +(s - 0.15).toFixed(2)))} className={btnClass} title="Thu nhỏ ảnh"><ZoomOut size={13} /></button>
                    <button onClick={() => setImageScale(s => Math.min(2.5, +(s + 0.15).toFixed(2)))} className={btnClass} title="Phóng to ảnh"><ZoomIn size={13} /></button>
                </div>

                <div ref={rightPanelRef} className="flex-1 overflow-y-auto overflow-x-auto bg-gray-50 dark:bg-slate-950">
                    <div className="px-3 py-3 pb-20 space-y-5">
                        {imageNodes.map(node => {
                            const imgs = nodeImageMap.get(node.id)!;
                            const isSelected = selectedNodeId === node.id;
                            return (
                                <div
                                    key={node.id}
                                    ref={el => { rightRefs.current[node.id] = el; }}
                                    className="cursor-pointer group"
                                    onClick={(e) => handleRightClick(node, e)}
                                >
                                    {node.title && (
                                        <div className={`text-[11px] font-medium px-0.5 pb-1 truncate ${
                                            isSelected ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'
                                        }`}>
                                            {node.title}
                                        </div>
                                    )}
                                    <div
                                        className={`rounded-lg overflow-hidden relative transition-all mx-auto ${
                                            isSelected
                                                ? 'border border-blue-200 dark:border-blue-800 shadow-sm shadow-blue-50 dark:shadow-blue-900/10'
                                                : 'border border-gray-100 dark:border-slate-800 group-hover:border-gray-200 dark:group-hover:border-slate-700'
                                        }`}
                                        style={{ width: `${imageScale * 100}%` }}
                                    >
                                        <button
                                            onClick={e => { e.stopPropagation(); onExpandNote(node.id); }}
                                            className="absolute top-1.5 right-1.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 text-gray-600 dark:text-gray-300 shadow-sm"
                                        >
                                            <Maximize2 size={11} />
                                        </button>
                                        {imgs.map((src, i) => (
                                            <img
                                                key={i}
                                                src={src}
                                                alt=""
                                                loading="lazy"
                                                className={`w-full object-contain bg-white dark:bg-slate-800 block ${i > 0 ? 'border-t border-gray-100 dark:border-slate-700' : ''}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                        {imageNodes.length === 0 && (
                            <div className="text-center text-gray-400 py-20 text-sm px-4">
                                Chưa có screenshot nào.<br />
                                <span className="text-xs">Chụp màn hình từ PDF viewer để xem ở đây.</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}));

FlowView.displayName = 'FlowView';
